import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    Loader2, Check, X, LogIn, Users, FileText,
    CreditCard, Ticket, LogOut, AlertCircle, Mail, UserCircle,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRIMARY = "#2D5BFF";
const PRIMARY_DARK = "#1B3DBF";
const GRADIENT = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;
const MASCOT_URL = "https://res.cloudinary.com/duzn3zaun/image/upload/w_300,f_auto,q_auto/v1781116942/Logo-Hi_n4gruq.png";
const PAGE_BG = "linear-gradient(180deg, #E8EEFF 0%, #F4F6FF 60%, #FFFFFF 100%)";

// Confetti diamonds scattered around mascot
const CONFETTI = [
    { top: "8%",  left: "6%",   color: "#FF9B50", size: 12, rotate: 45  },
    { top: "4%",  left: "28%",  color: "#A855F7", size: 8,  rotate: 20  },
    { top: "18%", right: "8%",  color: "#60A5FA", size: 14, rotate: -30 },
    { top: "38%", right: "4%",  color: "#34D399", size: 9,  rotate: 15  },
    { top: "42%", left: "4%",   color: "#F472B6", size: 7,  rotate: 45  },
    { top: "2%",  right: "28%", color: "#FBBF24", size: 10, rotate: -20 },
    { top: "26%", left: "12%",  color: "#60A5FA", size: 7,  rotate: 30  },
    { top: "48%", right: "18%", color: "#A855F7", size: 6,  rotate: -45 },
];

// Wave decoration next to group name
const WaveLine = ({ flip = false }) => (
    <svg
        width="28" height="20" viewBox="0 0 28 20"
        style={{ transform: flip ? "scaleX(-1)" : "none", flexShrink: 0 }}
    >
        <path
            d="M2 10 Q7 3 14 10 Q21 17 26 10"
            stroke={PRIMARY} strokeWidth="2.5"
            fill="none" strokeLinecap="round"
        />
    </svg>
);

// Group type label
const GROUP_TYPE_LABELS = {
    household: "household", trip: "trip",
    event: "event", community: "community", custom: "custom",
};

// ── Feature icon card ────────────────────────────────────────────────────────
const FeatureCard = ({ icon: Icon, label, bg, iconColor }) => (
    <div
        className="flex flex-col items-center justify-center gap-2"
        style={{
            flex: 1, padding: "14px 8px", borderRadius: 18,
            backgroundColor: bg,
        }}
    >
        <div
            className="flex items-center justify-center"
            style={{ width: 44, height: 44, borderRadius: 14, backgroundColor: "rgba(255,255,255,0.85)" }}
        >
            <Icon className="w-5 h-5" style={{ color: iconColor }} />
        </div>
        <p style={{ color: "#374151", fontSize: 11, fontWeight: 600, fontFamily: "DM Sans, sans-serif", textAlign: "center", lineHeight: 1.3 }}>
            {label}
        </p>
    </div>
);

// ── Email display row ────────────────────────────────────────────────────────
const EmailRow = ({ email, icon: Icon, iconColor = PRIMARY }) => (
    <div
        className="flex items-center gap-3"
        style={{
            backgroundColor: "#FFFFFF", borderRadius: 14, padding: "14px 16px",
            border: "1.5px solid #E5E7EB",
        }}
    >
        <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: "#EEF2FF" }}
        >
            <Icon className="w-5 h-5" style={{ color: iconColor }} />
        </div>
        <p style={{ color: "#111827", fontSize: 15, fontWeight: 700, fontFamily: "DM Sans, sans-serif", wordBreak: "break-all" }}>
            {email}
        </p>
    </div>
);

// ── Dashed "But" divider ─────────────────────────────────────────────────────
const ButDivider = () => (
    <div className="flex items-center gap-3" style={{ margin: "4px 0" }}>
        <div style={{ flex: 1, borderTop: "1.5px dashed #C7D2FE" }} />
        <span style={{
            padding: "4px 14px", borderRadius: 999,
            backgroundColor: "#EEF2FF", color: PRIMARY,
            fontSize: 13, fontWeight: 700, fontFamily: "DM Sans, sans-serif",
            border: "1.5px solid #C7D2FE",
        }}>
            But
        </span>
        <div style={{ flex: 1, borderTop: "1.5px dashed #C7D2FE" }} />
    </div>
);

// ── Initials avatar ──────────────────────────────────────────────────────────
const InitialsAvatar = ({ name, size = 44 }) => {
    const initials = (name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    return (
        <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: size, height: size, borderRadius: 999, background: GRADIENT, color: "#FFFFFF", fontWeight: 700, fontSize: 16, fontFamily: "Manrope, sans-serif" }}
        >
            {initials}
        </div>
    );
};

export default function AcceptInvitePage() {
    const { token } = useParams();
    const { user: authUser, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [inviteLoading, setInviteLoading] = useState(true);
    const [invite, setInvite] = useState(null);
    const [inviteError, setInviteError] = useState(null);
    const [accepting, setAccepting] = useState(false);
    const [declining, setDeclining] = useState(false);
    const [signingOut, setSigningOut] = useState(false);

    const fetchInvite = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/groups/invite-info/${token}`);
            if (res.data.success) setInvite(res.data.invite);
            else setInviteError(res.data.error || "Invite not found");
        } catch {
            setInviteError("Couldn't load invite details");
        } finally {
            setInviteLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchInvite(); }, [fetchInvite]);

    // Determine states (wait for both auth and invite to load)
    const isLoggedIn = !!authUser;
    const emailMatches = !invite?.email || !authUser?.email ||
        invite.email.toLowerCase() === authUser.email.toLowerCase();
    const showMismatch = isLoggedIn && !!invite?.email && !emailMatches;

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleAccept = async () => {
        setAccepting(true);
        try {
            const res = await axios.post(`${API}/groups/accept-invite`, { token }, { withCredentials: true });
            if (res.data.success && res.data.group_id) {
                navigate(`/groups/${res.data.group_id}`, { replace: true });
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (detail === "wrong_account") {
                toast.error("This invite was sent to a different account");
            } else {
                toast.error(typeof detail === "string" ? detail : "Couldn't accept invite");
            }
        } finally {
            setAccepting(false);
        }
    };

    const handleDecline = async () => {
        setDeclining(true);
        try {
            await axios.post(`${API}/groups/decline-invite`, { token }, { withCredentials: true });
            toast.success("Invite declined");
            navigate("/dashboard", { replace: true });
        } catch {
            toast.error("Couldn't decline invite");
        } finally {
            setDeclining(false);
        }
    };

    const handleSignOut = async () => {
        setSigningOut(true);
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch { /* ignore */ }
        window.location.replace("/login"); // hard redirect — no auth context flicker
    };

    const handleSignIn = () => {
        // Use localStorage (not sessionStorage) — survives OAuth redirect chain on all browsers
        try {
            localStorage.setItem("mb_pending_invite", JSON.stringify({ token, ts: Date.now() }));
        } catch { /* ignore */ }
        navigate("/login");
    };

    // ── Shared page wrapper ───────────────────────────────────────────────────
    const PageWrapper = ({ children }) => (
        <div
            className="min-h-screen flex flex-col items-center justify-start"
            style={{ background: PAGE_BG, paddingBottom: 40 }}
        >
            <div
                className="w-full flex flex-col items-center"
                style={{ maxWidth: 420, padding: "32px 20px 0" }}
            >
                {children}
            </div>
        </div>
    );

    // ── Loading ───────────────────────────────────────────────────────────────
    if (authLoading || inviteLoading) {
        return (
            <PageWrapper>
                <div className="flex items-center justify-center" style={{ minHeight: 300 }}>
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: PRIMARY }} />
                </div>
            </PageWrapper>
        );
    }

    // ── Error state ───────────────────────────────────────────────────────────
    if (inviteError) {
        return (
            <PageWrapper>
                <img src={MASCOT_URL} alt="My Buddy" style={{ width: 160, height: "auto", marginBottom: 16 }} />
                <h2 style={{ color: "#111827", fontSize: 22, fontWeight: 700, fontFamily: "Manrope, sans-serif", textAlign: "center", marginBottom: 8 }}>
                    Invite not found
                </h2>
                <p style={{ color: "#6B7280", fontSize: 14, fontFamily: "DM Sans, sans-serif", textAlign: "center", lineHeight: 1.6, marginBottom: 24 }}>
                    This invite may have already been used, cancelled, or the link is incorrect.
                </p>
                <button
                    onClick={() => navigate("/dashboard")}
                    style={{ height: 48, paddingLeft: 28, paddingRight: 28, borderRadius: 14, background: GRADIENT, color: "#FFFFFF", fontSize: 15, fontWeight: 700, fontFamily: "DM Sans, sans-serif", boxShadow: "0 8px 20px rgba(45,91,255,0.28)" }}
                >
                    Go to My Buddy
                </button>
                <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 28 }}>My Buddy | We Built Here</p>
            </PageWrapper>
        );
    }

    // ── Mismatch state ────────────────────────────────────────────────────────
    if (showMismatch) {
        return (
            <PageWrapper>
                {/* Mascot with "?" speech bubble */}
                <div style={{ position: "relative", marginBottom: 8, marginTop: 8 }}>
                    <img src={MASCOT_URL} alt="My Buddy confused" style={{ width: 180, height: "auto", display: "block" }} />
                    {/* Speech bubble */}
                    <div
                        style={{
                            position: "absolute", top: 0, left: -10,
                            width: 52, height: 52, borderRadius: "50% 50% 50% 10%",
                            backgroundColor: "#FFFFFF",
                            boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >
                        <span style={{ color: PRIMARY, fontSize: 24, fontWeight: 900, lineHeight: 1 }}>?</span>
                    </div>
                </div>

                {/* Title */}
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                    <p style={{ color: "#111827", fontSize: 24, fontWeight: 800, fontFamily: "Manrope, sans-serif", lineHeight: 1.25, marginBottom: 2 }}>
                        Oops! You're using
                    </p>
                    <p style={{ color: PRIMARY, fontSize: 24, fontWeight: 800, fontFamily: "Manrope, sans-serif", lineHeight: 1.25 }}>
                        a different account.
                    </p>
                </div>

                {/* Email comparison card */}
                <div
                    style={{
                        backgroundColor: "#F8FAFF", borderRadius: 20, padding: 20,
                        border: "1.5px solid #E0E7FF", width: "100%", marginBottom: 20,
                    }}
                >
                    <p style={{ color: "#6B7280", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginBottom: 10, textAlign: "center" }}>
                        This invitation was sent to
                    </p>
                    <EmailRow email={invite.email} icon={Mail} />

                    <ButDivider />

                    <p style={{ color: "#6B7280", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginBottom: 10, marginTop: 4, textAlign: "center" }}>
                        You're currently signed in as
                    </p>
                    <EmailRow email={authUser.email} icon={UserCircle} iconColor="#6B7280" />
                </div>

                {/* Info box */}
                <div
                    className="flex items-start gap-3"
                    style={{ backgroundColor: "#EEF2FF", borderRadius: 16, padding: "14px 16px", width: "100%", marginBottom: 20 }}
                >
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: PRIMARY }} />
                    <p style={{ color: "#374151", fontSize: 13, fontFamily: "DM Sans, sans-serif", lineHeight: 1.6 }}>
                        To accept this invitation, please{" "}
                        <button onClick={handleSignOut} style={{ color: PRIMARY, fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>
                            sign out
                        </button>{" "}
                        and open the invitation link again using the correct account.
                    </p>
                </div>

                {/* Sign out button */}
                <button
                    onClick={handleSignOut}
                    disabled={signingOut}
                    className="w-full flex items-center justify-center gap-2"
                    style={{
                        height: 54, borderRadius: 16, background: GRADIENT,
                        color: "#FFFFFF", fontSize: 16, fontWeight: 700,
                        fontFamily: "DM Sans, sans-serif", marginBottom: 16,
                        opacity: signingOut ? 0.7 : 1,
                        boxShadow: "0 8px 20px rgba(45,91,255,0.28)",
                        width: "100%",
                    }}
                >
                    {signingOut ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />}
                    {signingOut ? "Signing out…" : "Sign Out"}
                </button>

                {/* Help text */}
                <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#9CA3AF" }} />
                    <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>
                        Need help? Ask the person who invited you to resend the invitation.
                    </p>
                </div>

                <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 24 }}>
                    My Buddy | We Built Here
                </p>
            </PageWrapper>
        );
    }

    // ── Main invite state ─────────────────────────────────────────────────────
    const typeLabel = GROUP_TYPE_LABELS[invite.group_type] || "custom";

    return (
        <PageWrapper>
            {/* Mascot + confetti */}
            <div style={{ position: "relative", marginBottom: 4, marginTop: 8, width: 200, height: 180 }}>
                {CONFETTI.map((c, i) => (
                    <div
                        key={i}
                        style={{
                            position: "absolute",
                            top: c.top, left: c.left, right: c.right,
                            width: c.size, height: c.size,
                            backgroundColor: c.color,
                            borderRadius: 3,
                            transform: `rotate(${c.rotate}deg)`,
                            opacity: 0.85,
                        }}
                    />
                ))}
                <img
                    src={MASCOT_URL}
                    alt="My Buddy waving"
                    style={{
                        width: 160, height: "auto",
                        position: "absolute", bottom: 0,
                        left: "50%", transform: "translateX(-50%)",
                    }}
                />
            </div>

            {/* Invitation text */}
            <p style={{ color: "#6B7280", fontSize: 16, fontFamily: "DM Sans, sans-serif", marginBottom: 8 }}>
                You're invited to join
            </p>

            {/* Group name with wave decorations */}
            <div className="flex items-center gap-3" style={{ marginBottom: 24 }}>
                <WaveLine />
                <h1 style={{
                    color: PRIMARY, fontSize: 32, fontWeight: 900,
                    fontFamily: "Manrope, sans-serif", letterSpacing: -0.5,
                    textAlign: "center",
                }}>
                    {invite.group_name}
                </h1>
                <WaveLine flip />
            </div>

            {/* Feature icons */}
            <div className="flex gap-3 w-full" style={{ marginBottom: 24 }}>
                <FeatureCard icon={FileText}   label="Receipts"      bg="#EEF2FF" iconColor={PRIMARY}   />
                <FeatureCard icon={CreditCard} label="Loyalty cards" bg="#EEF2FF" iconColor="#7C3AED"  />
                <FeatureCard icon={Ticket}     label="Vouchers"      bg="#EEF2FF" iconColor="#059669"  />
            </div>

            {/* White card */}
            <div
                style={{
                    backgroundColor: "#FFFFFF", borderRadius: 24,
                    padding: 20, width: "100%",
                    boxShadow: "0 8px 32px rgba(15,23,42,0.08)",
                    border: "1px solid #F3F4F6",
                }}
            >
                {/* Inviter info */}
                <div className="flex items-start gap-3" style={{ marginBottom: 16 }}>
                    <InitialsAvatar name={invite.invited_by_name} />
                    <p style={{ color: "#374151", fontSize: 14, fontFamily: "DM Sans, sans-serif", lineHeight: 1.6, paddingTop: 2 }}>
                        <strong style={{ color: "#111827" }}>{invite.invited_by_name}</strong> has invited you to share receipts, cards, and vouchers in this {typeLabel} circle.
                    </p>
                </div>

                {/* Contributor badge */}
                <div
                    className="flex items-center justify-center gap-2"
                    style={{ backgroundColor: "#EEF2FF", borderRadius: 12, padding: "10px 16px", marginBottom: 16 }}
                >
                    <Users className="w-4 h-4" style={{ color: PRIMARY }} />
                    <span style={{ color: PRIMARY, fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif" }}>
                        You'll join as a contributor
                    </span>
                </div>

                {isLoggedIn ? (
                    <>
                        {/* Accept button */}
                        <button
                            onClick={handleAccept}
                            disabled={accepting}
                            className="w-full flex items-center justify-center gap-2"
                            style={{
                                height: 54, borderRadius: 16, background: GRADIENT,
                                color: "#FFFFFF", fontSize: 16, fontWeight: 700,
                                fontFamily: "DM Sans, sans-serif", marginBottom: 10,
                                opacity: accepting ? 0.7 : 1,
                                boxShadow: "0 8px 20px rgba(45,91,255,0.28)",
                            }}
                        >
                            {accepting
                                ? <Loader2 className="w-5 h-5 animate-spin" />
                                : <span style={{ fontSize: 18 }}>✦</span>}
                            {accepting ? "Joining…" : "Accept invite"}
                        </button>

                        {/* Decline button */}
                        <button
                            onClick={handleDecline}
                            disabled={declining}
                            className="w-full flex items-center justify-center gap-2"
                            style={{
                                height: 48, borderRadius: 14,
                                border: "1.5px solid #E5E7EB", backgroundColor: "#FFFFFF",
                                color: "#6B7280", fontSize: 15, fontWeight: 600,
                                fontFamily: "DM Sans, sans-serif",
                                opacity: declining ? 0.7 : 1,
                            }}
                        >
                            {declining ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                            {declining ? "Declining…" : "Decline"}
                        </button>
                    </>
                ) : (
                    <button
                        onClick={handleSignIn}
                        className="w-full flex items-center justify-center gap-2"
                        style={{
                            height: 54, borderRadius: 16, background: GRADIENT,
                            color: "#FFFFFF", fontSize: 16, fontWeight: 700,
                            fontFamily: "DM Sans, sans-serif",
                            boxShadow: "0 8px 20px rgba(45,91,255,0.28)",
                        }}
                    >
                        <LogIn className="w-5 h-5" />
                        Sign in to accept
                    </button>
                )}
            </div>

            {/* Footer */}
            <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 24 }}>
                My Buddy | We Built Here
            </p>
        </PageWrapper>
    );
}